/**
 * Module-level storage for the survey tab Window handle.
 * Needed because the tab is opened in SurveyStartModal but accessed
 * in SurveyWaitingPage after a React Router navigation (which cannot
 * pass a Window object via URL params or sessionStorage).
 */
let _handle: Window | null = null;

export function setSurveyTabHandle(w: Window | null) { _handle = w; }
export function getSurveyTabHandle(): Window | null { return _handle; }
export function clearSurveyTabHandle() { _handle = null; }

/**
 * Device-aware survey launch strategy.
 *
 * On desktop we open the survey in a new tab and babysit it from
 * SurveyWaitingPage. On mobile / in-app browsers (Instagram, TikTok,
 * Facebook, etc.) the programmatic `window.open('', '_blank')` popup is
 * frequently blocked or mishandled, dropping the member into the waiting
 * page's "We lost track of your survey" state. For that traffic we navigate
 * the SAME tab full-page to the survey and let the provider redirect bring
 * the member back to a result route (/survey/complete | /survey/screenout |
 * /survey/security-terminate) — no popup, no tab handle, no polling.
 *
 * Returns true when the same-tab strategy should be used: a phone/tablet
 * user agent, OR a known in-app webview wrapper on any UA.
 */
export function shouldLaunchSameTab(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // Phone / tablet user agents.
  if (/iphone|ipad|ipod|android/i.test(ua)) return true;
  // In-app webview wrappers (these can present desktop-class UA strings):
  //  - Facebook (FBAN/FBAV/FB_IAB), Instagram, TikTok / musical.ly,
  //  - Snapchat, Twitter/X, Pinterest, LinkedIn, Line, WeChat (MicroMessenger),
  //  - generic Android WebView marker "; wv)".
  if (/FBAN|FBAV|FB_IAB|Instagram|TikTok|musical_ly|BytedanceWebview|Snapchat|Twitter|Pinterest|LinkedInApp|\bLine\/|MicroMessenger|; wv\)/i.test(ua)) {
    return true;
  }
  return false;
}

/**
 * Lossless storage for the RAW signed survey entry URL, keyed by session id.
 *
 * The PS entry URL is signed with a SHA1 over its exact bytes (ps_s_hash).
 * Routing it through the /waiting `?url=` query param double-encodes the
 * separators (URLSearchParams.set + React Router string-path navigate), and
 * `searchParams.get('url')` only decodes one layer — so window.open would send
 * PS escaped bytes (`1438%3D120%2C111…`) that no longer match the signature and
 * fail PS_Supply_Inbound_Hash_Security_FAIL. Unlike a Window handle, a STRING
 * can live in sessionStorage losslessly (no URL encoding), so we keep the raw
 * bytes in an in-memory map (fast path) with a sessionStorage fallback that
 * survives the navigate and a page refresh. See
 * docs/investigations/ps-inbound-hash-live-error-2026-05-30.md
 * ("Waiting-page redirect mutation").
 */
const _entryUrls = new Map<string, string>();
const ENTRY_URL_KEY = (sessionId: string) => `so_entry_url:${sessionId}`;

export function setSurveyEntryUrl(sessionId: string, url: string) {
  _entryUrls.set(sessionId, url);
  try { sessionStorage.setItem(ENTRY_URL_KEY(sessionId), url); } catch { /* storage unavailable */ }
}

export function getSurveyEntryUrl(sessionId: string): string | null {
  const inMemory = _entryUrls.get(sessionId);
  if (inMemory != null) return inMemory;
  try { return sessionStorage.getItem(ENTRY_URL_KEY(sessionId)); } catch { return null; }
}

export function clearSurveyEntryUrl(sessionId: string) {
  _entryUrls.delete(sessionId);
  try { sessionStorage.removeItem(ENTRY_URL_KEY(sessionId)); } catch { /* storage unavailable */ }
}
